/* eslint-disable import/no-unresolved */
import { useEffect } from 'react';

import { useHistory } from 'react-router';

import { changeBasketUnderlyingAsset } from 'actions/basketOrders';
import { updateOptionChainAssetSymbol } from 'actions/optionChain';
import { getOrderLeverage } from 'actions/orderbook';
import {
  setOpenPositionsForAnalysis,
  setPositionAnalyticsModalToggleState,
} from 'actions/positionAnalyse';
import { getTradePreferences } from 'actions/trade';
import {
  getAccountToken,
  getKycStatus,
  logout,
  switchToMainAccount,
  updateUserPreference,
  userNativeLoginSuccess,
} from 'actions/user';
import { TAB_CHANGE } from 'actionTypes/appDrawer';
import TRADE from 'actionTypes/trade';
import { postMessageToMobileApp } from 'components/app/helpers';
import { tabNames } from 'components/MobileHolding/utils';
import { isRNWebview } from 'constants/constants';
import { POST_MESSAGE_TO_MOBILE_EVENTS, STORAGE_KEYS } from 'constants/enums';
import routes from 'constants/routes';
import { refreshCacheAndReload } from 'helpers/mixpanel-init';
import { addLogsToSentry } from 'helpers/sentry';
import { clearWorkboxCache, refreshPage } from 'helpers/utils';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import { allOpenPositionsSelector } from 'selectors/holdingsSelectors';
import { selectedProductState } from 'selectors/tradeSelectors';
import { useAppDispatch } from 'storeHooks';
import StoreProxy from 'storeProxy';
import { resetScalpingStore } from 'stores/scalping/scalping.selectors';
import { useScalpingStoreBase } from 'stores/scalping/scalping.store';

import { useBiometrics } from './componentHooks/useBiometrics/useBiometrics.Provider';
import { useAutoMobileOTPVerification } from './useAutoMobileOTPVerification';
import useCustomEvent, { EventName } from './useCustomEvent';
import { useOAuthLogin } from './useOAuthLogin';
import useTheme from './useTheme';

const RN_MESSAGE_EVENT_TYPE = 'RN_MESSAGE_EVENTS';

const EVENT_TYPES = {
  BIOMETRICS_MESSAGES: 'BIOMETRICS_MESSAGES',
  KEYBOARD_HEIGHT: 'KEYBOARD_HEIGHT',
  MOBILE_DEVICE_ID: 'MOBILE_DEVICE_ID',
  OAUTH_SIGNIN_RESPONSE: 'OAUTH_SIGNIN_RESPONSE',
  AUTH_TOKEN: 'AUTH_TOKEN',
  DELETED_ACCOUNT_EXISTS: 'DELETED_ACCOUNT_EXISTS',
  NATIVE_THEME_SET: 'NATIVE_THEME_SET',
  CLEAR_CACHE: 'CLEAR_CACHE',
  NAVIGATE: 'NAVIGATE',
  FAVORITE_UPDATE: 'FAVORITE_UPDATE',
  NOTIFICATIONS_PERMISSION_ACCESS: 'NOTIFICATIONS_PERMISSION_ACCESS',
  PHONE_NUMBER_SELECTED: 'PHONE_NUMBER_SELECTED',
  MOBILE_OTP_HASH_CODE: 'MOBILE_OTP_HASH_CODE',
  PAY_OFF: 'PAY_OFF',
  MOBILE_OTP_RECEIVED: 'MOBILE_OTP_RECEIVED',
  LOGOUT: 'LOGOUT',
  USER_PROFILE_UPDATE: 'USER_PROFILE_UPDATE',
  UPDATE_MARGIN_MODE: 'UPDATE_MARGIN_MODE',
  SWITCH_ACCOUNT: 'SWITCH_ACCOUNT',
  OPTIONS_CHAIN_UNDERLYING_ASSETS: 'OPTIONS_CHAIN_UNDERLYING_ASSETS',
  EXIT_FULL_SCREEN: 'EXIT_FULL_SCREEN',
  UPDATE_SHOW_EQUITY: 'UPDATE_SHOW_EQUITY',
  CLOSE_MODAL: 'CLOSE_MODAL',
} as const;

const useRNMessageEvents = () => {
  const { onMessageFromApp: onBiometricsMessageFromApp } = useBiometrics();
  const { setOAuthLoginState } = useOAuthLogin();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { setTheme } = useTheme();
  const { setOTP, setHashCode } = useAutoMobileOTPVerification();

  const dispatchNotificationPermission = useCustomEvent(
    EventName.NOTIFICATION_PERMISSION
  );

  const resetRebateAmount = useScalpingStoreBase(resetScalpingStore);

  // const loggedUserType = getLoggedUserType(user);

  useEffect(() => {
    const onMessageHandler = (event: MessageEvent) => {
      const handleEvent = async () => {
        if (!isRNWebview() || event?.data?.type !== RN_MESSAGE_EVENT_TYPE) return;

        const { name, data } = event?.data?.payload ?? {};

        switch (name) {
          case EVENT_TYPES.BIOMETRICS_MESSAGES: {
            onBiometricsMessageFromApp(data);
            break;
          }

          case EVENT_TYPES.OAUTH_SIGNIN_RESPONSE: {
            setOAuthLoginState(data);
            break;
          }

          case EVENT_TYPES.AUTH_TOKEN: {
            const userDetails = data?.userData ?? {};
            dispatch(userNativeLoginSuccess(userDetails));
            if (data?.deviceId) {
              localStorage.setItem('mobileDeviceId', data.deviceId);
            }
            break;
          }

          case EVENT_TYPES.CLEAR_CACHE: {
            refreshCacheAndReload('Mobile App');
            break;
          }

          case EVENT_TYPES.USER_PROFILE_UPDATE: {
            if (data.isTradePreference) {
              dispatch(getTradePreferences());
            } else {
              dispatch(updateUserPreference(data, { disable_notification: true }));
            }

            break;
          }

          case EVENT_TYPES.FAVORITE_UPDATE: {
            dispatch(getKycStatus());
            break;
          }

          case EVENT_TYPES.LOGOUT: {
            // @ts-ignore
            dispatch(logout()).then(() => {
              clearWorkboxCache();
              resetRebateAmount();
              if (isRNWebview()) {
                setOAuthLoginState();
              }
            });
            break;
          }

          case EVENT_TYPES.DELETED_ACCOUNT_EXISTS: {
            const { email, userId } = data;

            const url = `${routes.recoverDeleteAccount}?e=${encodeURIComponent(
              btoa(email)
            )}&user_id=${userId}`;
            history.push(url);
            break;
          }

          case EVENT_TYPES.NATIVE_THEME_SET: {
            const { theme } = data;
            setTheme(theme, false);
            break;
          }

          case EVENT_TYPES.MOBILE_DEVICE_ID: {
            localStorage.setItem('mobileDeviceId', data.deviceId);
            break;
          }

          case EVENT_TYPES.OPTIONS_CHAIN_UNDERLYING_ASSETS: {
            const { asset } = data;
            dispatch(updateOptionChainAssetSymbol(asset));
            dispatch(changeBasketUnderlyingAsset(asset));
            break;
          }

          case EVENT_TYPES.NOTIFICATIONS_PERMISSION_ACCESS: {
            dispatchNotificationPermission(data.granted as boolean);
            break;
          }

          case EVENT_TYPES.CLOSE_MODAL: {
            history.replace({
              pathname: history.location.pathname,
              search: '',
            });
            break;
          }

          case EVENT_TYPES.NAVIGATE: {
            try {
              const pathname = data.route;
              const params = pathname.split('?')?.[1];
              const queryString = params ? `?${params}` : '';

              if (pathname === '/futures/markets') {
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.MARKETS,
                });
              }

              if (
                pathname.includes(`${routes.futures.trade}`) ||
                pathname.includes(`${routes.options.trade}`) ||
                pathname.includes(`${routes.move_options.trade}`)
              ) {
                history.push(data.route);
                // @ts-ignore
                dispatch({
                  type: TAB_CHANGE,
                  payload: queryString.includes('chart')
                    ? APP_DRAWER_TAB_ID_MAPPING.CHART_ORDERBOOK
                    : APP_DRAWER_TAB_ID_MAPPING.TRADE,
                });
              } else if (pathname === '/trade') {
                history.push('/futures/trade');
                // @ts-ignore
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.TRADE,
                });
              } else if (pathname.includes('/chart')) {
                history.push('/futures/trade');
                // @ts-ignore
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.CHART_ORDERBOOK,
                });
              } else if (pathname.includes('/positions')) {
                if (queryString.includes('wallet_active_tab')) {
                  localStorage.setItem(
                    STORAGE_KEYS.MOBILE_HOLDING_ACTIVE_TAB,
                    JSON.stringify(tabNames[0])
                  );
                  history.push(
                    `${routes.account.positions}?wallet_active_tab=risk and margin`
                  );
                } else {
                  history.push(`/futures/trade${queryString}`);
                  localStorage.setItem(
                    STORAGE_KEYS.MOBILE_HOLDING_ACTIVE_TAB,
                    JSON.stringify(tabNames[1])
                  );
                }
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.POSITIONS,
                });
              } else if (pathname.includes('/balances')) {
                history.push(`/futures/trade${queryString}`);
                localStorage.setItem(
                  STORAGE_KEYS.MOBILE_HOLDING_ACTIVE_TAB,
                  JSON.stringify(tabNames[0])
                );
                dispatch({
                  type: TAB_CHANGE,
                  payload: APP_DRAWER_TAB_ID_MAPPING.POSITIONS,
                });
              } else {
                history.push(data.route);
              }
            } catch (error) {
              console.error('Invalid message from RN', error);
            }
            break;
          }

          case EVENT_TYPES.KEYBOARD_HEIGHT: {
            break;
          }

          case EVENT_TYPES.MOBILE_OTP_HASH_CODE: {
            const { hashCode } = data;
            setHashCode(hashCode);
            break;
          }

          case EVENT_TYPES.UPDATE_SHOW_EQUITY: {
            const { showAmount } = data;
            const serializedValue = JSON.stringify(showAmount);
            localStorage.setItem(STORAGE_KEYS.SHOW_BALANCE, serializedValue);
            window.dispatchEvent(
              new StorageEvent('storage', {
                key: STORAGE_KEYS.SHOW_BALANCE,
                newValue: serializedValue,
              })
            );
            break;
          }

          case EVENT_TYPES.MOBILE_OTP_RECEIVED: {
            const { otp } = data;
            setOTP(otp);
            postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.REMOVE_OTP_LISTENER);
            break;
          }
          case EVENT_TYPES.EXIT_FULL_SCREEN: {
            document.exitFullscreen?.();
            break;
          }

          case EVENT_TYPES.PAY_OFF: {
            const { baseSymbol } = data;
            const allOpenPositions = StoreProxy.selector(allOpenPositionsSelector);
            const positions = allOpenPositions.filter(
              openPosition => openPosition.product.underlying_asset.symbol === baseSymbol
            );
            dispatch(setOpenPositionsForAnalysis(positions));

            // Mirror Risk View behavior: open modal; only navigate if not already on positions page
            dispatch(setPositionAnalyticsModalToggleState(true));

            history.push(routes.positionAnalytics);
            break;
          }

          case EVENT_TYPES.UPDATE_MARGIN_MODE: {
            try {
              const { marginMode, userId } = data;

              dispatch({
                type: TRADE.UPDATE_MARGIN_MODE.SUCCESS,
                result: {
                  result: {
                    margin_mode: marginMode,
                    id: userId,
                  },
                },
                params: {
                  margin_mode: marginMode,
                  showChangeNotif: true,
                },
              });

              // Refresh leverage data for the product if provided
              const selectedProduct = StoreProxy.selector(selectedProductState);
              if (selectedProduct?.id) {
                dispatch(getOrderLeverage(selectedProduct.id));
              }
            } catch (error) {
              addLogsToSentry(
                null,
                error instanceof Error
                  ? error
                  : new Error('Error handling UPDATE_MARGIN_MODE from RN'),
                { extra: { marginMode: data?.marginMode, userId: data?.userId } },
                { source: 'useRNMessageEvents', eventType: 'UPDATE_MARGIN_MODE' }
              );
            }
            break;
          }

          case EVENT_TYPES.SWITCH_ACCOUNT: {
            const { userId, isMainAccount } = data;

            if (isMainAccount) {
              dispatch(switchToMainAccount());
              postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGOUT, {
                isSwitchingAccount: true,
              });
              refreshPage();
            } else {
              dispatch(getAccountToken({ sub_account_user_id: userId }))
                .then(() => {
                  postMessageToMobileApp(POST_MESSAGE_TO_MOBILE_EVENTS.LOGOUT, {
                    isSwitchingAccount: true,
                  });
                  refreshPage();
                })
                .catch(error => {
                  addLogsToSentry(
                    null,
                    error instanceof Error
                      ? error
                      : new Error('Error switching to sub-account'),
                    { extra: { userId, isMainAccount } },
                    { source: 'useRNMessageEvents', eventType: 'SWITCH_ACCOUNT' }
                  );
                });
            }
            break;
          }

          default: {
            break;
          }
        }
      };

      handleEvent().catch(error => {
        addLogsToSentry(null, error);
      });
    };

    window.addEventListener('message', onMessageHandler);

    return () => {
      window.removeEventListener('message', onMessageHandler);
    };
  }, [
    dispatch,
    history,
    onBiometricsMessageFromApp,
    setOAuthLoginState,
    setTheme,
    dispatchNotificationPermission,
    setOTP,
    setHashCode,
    resetRebateAmount,
  ]);
};

export default useRNMessageEvents;
export { RN_MESSAGE_EVENT_TYPE };
